import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, Stat, StatusPill, SandboxNotice, inputCls, fmtMoney, fmtDate } from "@/components/Peep";

export default function MembershipSettings() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price: "" });
  const [review, setReview] = useState(false);
  const load = useCallback(() => {
    api.get("/creator/memberships").then((r) => {
      setD(r.data);
      setForm({ name: r.data.plan.name, description: r.data.plan.description, price: r.data.plan.price ?? "" });
      setReview(false);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-sm text-[#a7a0af]">Loading…</p>;
  const { plan } = d;
  const p = form.price === "" ? null : Number(form.price);
  const priceErr = p === null ? null : !/^\d+(\.\d{1,2})?$/.test(String(form.price)) ? "Use a number with at most 2 decimals." : p < d.min_price || p > d.max_price ? `Between ${fmtMoney(d.min_price)} and ${fmtMoney(d.max_price)}.` : null;
  const changed = p !== plan.price;
  const save = async () => {
    try {
      const body = { name: form.name, description: form.description };
      if (form.price === "") body.clear_price = true; else body.price = p;
      await api.put("/creator/memberships", body);
      toast.success(changed && p !== null ? "Price published — new members pay this price" : "Membership saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div data-testid="creator-membership" className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold">Membership pricing</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">You set your own monthly price. Your membership unlocks your “Paid member” content; All Access members also get it.</p>
      </div>
      <SandboxNotice />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Current price" value={plan.price != null ? `${fmtMoney(plan.price)}/mo` : "Not set"} testid="membership-current-price" />
        <Stat label="Direct subscribers" value={d.direct_subscribers} testid="membership-subscribers" />
        <Stat label="Your share" value={`${d.creator_share_pct}%`} testid="membership-share" />
        <Stat label="Sandbox earnings" value={fmtMoney(d.sandbox_revenue.earnings)} testid="membership-earnings" />
      </div>
      <Panel title="Your plan" action={<StatusPill status={plan.purchasable ? "active" : "not purchasable"} testid="creator-plan-status" />}>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs text-[#a7a0af]">Name<input className={inputCls} data-testid="creator-plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label className="flex flex-col gap-1 text-xs text-[#a7a0af]">Monthly price (USD) — {fmtMoney(d.min_price)} to {fmtMoney(d.max_price)}, blank = not offered
            <input inputMode="decimal" className={inputCls} data-testid="creator-plan-price" value={form.price} onChange={(e) => { setForm({ ...form, price: e.target.value.replace(/[^\d.]/g, "") }); setReview(false); }} placeholder="Not set" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-[#a7a0af] md:col-span-2">Description<input className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
        </div>
        {priceErr && <p data-testid="creator-plan-price-error" className="mt-2 text-xs text-red-300">{priceErr}</p>}
        {review && p !== null && (
          <div data-testid="creator-plan-review" className="mt-4 rounded-xl border border-[#a03cff]/40 bg-[#3a0d6e]/30 p-3 text-xs text-[#e0c2ff]">
            New members will pay <b>{fmtMoney(p)}/month</b>, billed every 30 days until they cancel. On each payment you receive {d.creator_share_pct}% after processing fees ({fmtMoney(p * d.creator_share_pct / 100)} before fees); PEEPING keeps {d.platform_share_pct}%.
            {plan.price != null && d.direct_subscribers > 0 && <> Your {d.direct_subscribers} current subscriber(s) keep their existing price and are notified — their charges never change silently.</>}
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          {changed && p !== null && !review ? (
            <button disabled={!!priceErr} onClick={() => setReview(true)} data-testid="creator-plan-review-btn" className="btn-brand rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-40">Review price</button>
          ) : (
            <button disabled={!!priceErr} onClick={save} data-testid="creator-plan-save" className="btn-brand rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-40">{changed && p !== null ? "Publish price" : "Save"}</button>
          )}
        </div>
      </Panel>
      {d.price_history.length > 0 && (
        <Panel title="Price history">
          <ul data-testid="price-history" className="flex flex-col gap-1 text-xs text-[#c7c1cf]">
            {d.price_history.slice().reverse().map((h) => <li key={h.at}>{fmtDate(h.at)}: {h.from != null ? fmtMoney(h.from) : "not set"} → {h.to != null ? fmtMoney(h.to) : "not set"}</li>)}
          </ul>
        </Panel>
      )}
    </div>
  );
}
